import iconsPng from '@/assets/img/png/icons';
import svgIcons from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import SvgIcon from '@/components/atoms/SvgIcon';
import CircleFillOutWithText from '@/components/molecules/WellnessStateCircle/CircleFillOutWithText';
import {
  ORGANIZATION_CONTRACT_SERVICE_TYPES_DISPLAY_LABELS,
  PEER_SUPPORT_IMPLEMENTATION_PROGRAM_SUB_SERVICES,
  PEER_SUPPORT_IMPLEMENTATION_PROGRAM_SUB_SERVICES_DISPLAY_LABELS,
} from '@/constants';
import { NO_ID } from '@/constants/stringVars';
import useGetOrganizationActiveContract from '@/hooks/organization/getOrganizationActiveContract';
import ContactSalesModal from '@/modules/organization/ContactSalesModal';
import { useAppStore } from '@/store/useAppStore';
import colors from '@/theme/colors';
import { Center, HStack, Image, Progress, Text, VStack } from '@chakra-ui/react';
import { formatDate } from 'date-fns';
import React, { useMemo, useState } from 'react';

const OrganizationActiveContractPage = () => {
  const { user } = useAppStore();
  const { data: contractData, isLoading } = useGetOrganizationActiveContract(user?.user_id);
  const [peerSupportServices, nonPeerSupportSubServices, clinicalSupportServices] = useMemo(
    () => [
      contractData?.services.filter((service) => service.type === 'PEER_SUPPORT_IMPLEMENTATION_PROGRAM'),
      contractData?.services.filter(
        (service) =>
          service.type === 'WELLNESS_TRAINING_SESSIONS' ||
          service.type === 'PEER_SUPPORT_CERTIFICATIONS' ||
          service.type === 'PEER_SUPPORT_WORKSHOP',
      ),
      contractData?.services.filter(
        (service) =>
          service.type === 'PREPAID_THERAPY_SESSIONS' || service.type === 'PEER_SUPPORT_CLINICAL_CONSULTATIONS',
      ),
    ],
    [contractData],
  );

  const allPeerSupportSubServicesUsed = useMemo(
    () => peerSupportServices?.[0]?.subServices?.length === PEER_SUPPORT_IMPLEMENTATION_PROGRAM_SUB_SERVICES.length,
    [peerSupportServices],
  );

  const usedUpNonPeerSupportServices = useMemo(() => {
    const allUsedUpServices = nonPeerSupportSubServices
      ? [
          ...nonPeerSupportSubServices
            .filter((service) => service.used === service.total)
            .map((service) => ORGANIZATION_CONTRACT_SERVICE_TYPES_DISPLAY_LABELS[service.type]),
        ]
      : [];

    return allUsedUpServices;
  }, [nonPeerSupportSubServices]);

  const [isContactSalesModalOpen, setIsContactSalesModalOpen] = useState(false);

  if (isLoading) {
    return (
      <VStack marginTop={'22px'}>
        <LoadingLogo />
      </VStack>
    );
  }

  return (
    <VStack width={'100%'} marginTop={'40px'}>
      {!contractData ? (
        <VStack marginTop={'60px'} gap={'40px'}>
          <Image src={iconsPng.contractLocked} width={140} height={140} alt="info-eula-tooltip" />
          <Text variant={'loraMedium22'} color="text.mediumGray">
            Your organization doesn’t have an active contract.
          </Text>
        </VStack>
      ) : (
        <VStack width={'100%'}>
          <HStack width={'100%'} justifyContent={'space-between'}>
            <VStack align={'start'}>
              <Text variant={'loraSectionTitle'} color="text.darkBlue">
                Contract Utilization
              </Text>
              <HStack gap={'14px'}>
                <HStack gap={'4px'}>
                  <Text variant={'urbanistSemiBoldRegular'} color="text.mediumGray">
                    Last Update
                  </Text>
                  <Text variant={'urbanistSemiBoldRegular'} color="text.mediumBlue">
                    {formatDate(new Date(contractData?.updatedAt), 'd. MMM ‘yy')}
                  </Text>
                </HStack>
                <HStack gap={'4px'}>
                  <Text variant={'urbanistSemiBoldRegular'} color="text.mediumGray">
                    Contract End:
                  </Text>
                  <Text variant={'urbanistSemiBoldRegular'} color="text.mediumBlue">
                    {formatDate(new Date(contractData?.endDate), 'd. MMM ‘yy')}
                  </Text>
                </HStack>
              </HStack>
            </VStack>

            {/* TODO: will be added later */}
            {/* <CustomButton
            label="View Contract PDF"
            labelColor="text.darkBlue"
            onClick={() => setIsViewPDFModalOpen(true)}
          /> */}
          </HStack>

          {nonPeerSupportSubServices?.length && nonPeerSupportSubServices.length > 0 && (
            <VStack marginTop={'60px'} width={'100%'}>
              <Text variant={'loraMedium22'} marginBottom={'40px'}>
                Building a Better Team
              </Text>

              <HStack
                gap={'0px'}
                flexWrap={'wrap'}
                width={'100%'}
                alignItems={'start'}
                rowGap={'40px'}
                justifyContent={'center'}
              >
                {nonPeerSupportSubServices.map((service) => {
                  const total = service?.total ?? 1;
                  const usagePercentage = ((service.used ?? total / 20) / total) * 100;

                  const colorTheme =
                    usagePercentage >= 67 ? 'customRed' : usagePercentage >= 33 ? 'customYellow' : 'customTeal';

                  return (
                    <VStack key={service.type} width={'33.3%'} padding={'0 40px'} gap={'20px'}>
                      <Progress
                        colorScheme={colorTheme}
                        padding={'5px'}
                        background={'white'}
                        height={'24px'}
                        value={usagePercentage < 3 ? 3 : usagePercentage} // to have at least some fill-out of the progress bar
                        width={'100%'}
                        borderRadius={'20px'}
                        backgroundColor={colors.extra.white}
                        boxShadow="0px 0px 24px 0px #0041791A"
                      />
                      <VStack gap={'8px'} width={'100%'} alignItems={'center'}>
                        <Text variant={'urbanistSemiBoldLarge'} color={'text.darkBlue'}>
                          {ORGANIZATION_CONTRACT_SERVICE_TYPES_DISPLAY_LABELS[service.type]}
                        </Text>
                        <HStack>
                          <Text variant={'urbanistBold'} color={'text.darkBlue'}>
                            {service.used ?? 0}
                          </Text>
                          <Text variant={'urbanistBold'} color={'text.mediumGray'}>
                            / {service.total} Total
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  );
                })}
              </HStack>
            </VStack>
          )}

          {/* if we have the Peer Support Implementation service AND not all the sub-services have been utilized */}
          {peerSupportServices?.length && (
            <VStack marginTop={'80px'}>
              <Text variant={'loraMedium22'}>Peer Support Program Implementation Support</Text>

              {allPeerSupportSubServicesUsed ? (
                <VStack
                  boxShadow="0px 6.97px 34.86px 0px #00417930"
                  backgroundColor={'extra.white'}
                  padding={'20px'}
                  borderRadius={'8px'}
                  width={'400px'}
                  marginTop={'20px'}
                >
                  <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumGray'}>
                    Nice work, You have utilized your Peer Support Program Implementation Support services.
                  </Text>
                </VStack>
              ) : (
                <VStack
                  backgroundColor={'extra.white'}
                  borderRadius={'8px'}
                  boxShadow="0px 6.97px 34.86px 0px #00417930"
                  padding={'20px'}
                  gap={'12px'}
                  marginTop={'40px'}
                >
                  <Text
                    variant={'urbanistSemiBoldRegular'}
                    color={'text.mediumGray'}
                    maxWidth={'370px'}
                    marginBottom={'6px'}
                  >
                    Below you can see breakdown of services that were completed and that still aren’t.
                  </Text>

                  {PEER_SUPPORT_IMPLEMENTATION_PROGRAM_SUB_SERVICES.map((subService) => {
                    const isUsed = peerSupportServices[0].subServices?.includes(subService);

                    return (
                      <HStack key={subService} width={'100%'} gap={'14px'}>
                        <Center
                          borderRadius={'50%'}
                          width={'18px'}
                          height={'18px'}
                          backgroundColor={isUsed ? colors.primary[500] : colors.background.darkGray}
                          padding={'2px'}
                        >
                          {isUsed && (
                            <SvgIcon
                              color={colors.extra.white}
                              iconPath={svgIcons.checkSmall}
                              size={10}
                              style={{ paddingTop: '4px', paddingLeft: '2px' }}
                            />
                          )}
                        </Center>
                        <Text variant={'urbanistSemiBoldRegular'} color={isUsed ? 'primary.500' : 'text.mediumBlue'}>
                          {PEER_SUPPORT_IMPLEMENTATION_PROGRAM_SUB_SERVICES_DISPLAY_LABELS[subService]}
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              )}
            </VStack>
          )}

          {clinicalSupportServices && clinicalSupportServices.length > 0 && (
            <VStack marginTop={'80px'}>
              <Text variant={'loraMedium22'} marginBottom={'40px'}>
                Clinical Support
              </Text>

              <HStack width={'100%'} justifyContent={'center'} gap={'30px'}>
                {clinicalSupportServices.map((service) => {
                  const total = service.total ?? 1;
                  const usagePercentage = ((service?.used ?? total / 20) / total) * 100;

                  const circleColor =
                    usagePercentage >= 67
                      ? colors.extra.red
                      : usagePercentage >= 33
                        ? colors.extra.yellowStrong
                        : colors.extra.teal;

                  return (
                    <HStack key={service.type} width={'250px'} gap={'16px'} justifyContent={'start'}>
                      <CircleFillOutWithText
                        percentageFilled={usagePercentage}
                        svgId={service.type}
                        circleColor={circleColor}
                        showPercentageText={false}
                        circleDiameter={80}
                        centeredElement={
                          <VStack gap={'4px'}>
                            <Text variant={'urbanistExtraBold16'} color={'text.darkBlue'}>
                              {service?.used ?? 0}
                            </Text>
                            <Text variant={'urbanistExtraBoldSmall12'} color={'text.mediumGray'}>
                              / {service.total}
                            </Text>
                          </VStack>
                        }
                      />
                      <VStack align={'start'}>
                        <Text variant={'urbanistSemiBoldLarge'} color={'text.darkBlue'} textAlign={'start'}>
                          {ORGANIZATION_CONTRACT_SERVICE_TYPES_DISPLAY_LABELS[service.type]}
                        </Text>
                        <Text variant={'urbanistExtraBold'} color={'text.mediumGray'}>
                          Total
                        </Text>
                      </VStack>
                    </HStack>
                  );
                })}
              </HStack>
            </VStack>
          )}

          {(usedUpNonPeerSupportServices.length > 0 || allPeerSupportSubServicesUsed) && (
            <VStack marginTop={'100px'}>
              <Text variant={'loraMedium22'} marginBottom={'20px'}>
                Need more services or want to upgrade?
              </Text>
              {usedUpNonPeerSupportServices.map((usedUpService) => (
                <HStack gap={'4px'} key={usedUpService}>
                  <Text variant={'urbanistMediumLarge'} color={'text.mediumGray'}>
                    Nice work, You have utilized your
                  </Text>
                  <Text variant={'urbanistBold16'} color={'extra.red'}>
                    {usedUpService}
                  </Text>
                  <Text variant={'urbanistMediumLarge'} color={'text.mediumGray'}>
                    services!
                  </Text>
                </HStack>
              ))}

              <CustomButton
                label="Contact Sales"
                backgroundColor="primary.500"
                labelColor="extra.white"
                style={{ marginTop: usedUpNonPeerSupportServices.length > 0 ? '40px' : '10px' }}
                onClick={() => setIsContactSalesModalOpen(true)}
              />
            </VStack>
          )}
        </VStack>
      )}

      <ContactSalesModal
        contractId={contractData?.id ?? NO_ID}
        isOpen={isContactSalesModalOpen}
        onClose={() => setIsContactSalesModalOpen(false)}
      />
    </VStack>
  );
};

export default OrganizationActiveContractPage;
